import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Users, UserPlus, DollarSign, TrendingUp, AlertTriangle, CheckCircle, Clock, Eye, Bell, MessageSquare, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentClients, setRecentClients] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [smsModal, setSmsModal] = useState(null);
  const [smsText, setSmsText] = useState('');
  const [loading, setLoading] = useState(true);

  const isFinanceRole = ['aline'].includes(user?.role);
  const isHourlyEmployee = user?.role === 'office_admin'; // Only hourly employees get the time clock

  // Time clock state
  const [clockStatus, setClockStatus] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Live timer effect
  useEffect(() => {
    if (!clockStatus || clockStatus.status === 'not_started' || clockStatus.clock_out) return;
    const isOnLunch = clockStatus.lunch_out && !clockStatus.lunch_in;
    if (isOnLunch) return; // pause timer during lunch

    const clockIn = new Date(clockStatus.clock_in).getTime();
    let lunchDeduction = 0;
    if (clockStatus.lunch_out && clockStatus.lunch_in) {
      lunchDeduction = new Date(clockStatus.lunch_in).getTime() - new Date(clockStatus.lunch_out).getTime();
    }

    const tick = () => {
      const now = Date.now();
      const worked = now - clockIn - lunchDeduction;
      setElapsedTime(Math.max(0, Math.floor(worked / 1000)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [clockStatus]);

  const formatElapsed = (totalSec) => {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          axios.get(`${API_URL}/api/dashboard/stats`),
          axios.get(`${API_URL}/api/clients`)
        ];
        if (isFinanceRole) {
          requests.push(axios.get(`${API_URL}/api/payment-reminders`).catch(() => ({ data: [] })));
        }
        const results = await Promise.all(requests);
        setStats(results[0].data);
        setRecentClients(results[1].data.slice(0, 5));
        if (isFinanceRole && results[2]) {
          setReminders(results[2].data);
        }
        // Fetch time clock status and auto-clock-in ONLY for hourly employees (office_admin)
        if (isHourlyEmployee) {
          try {
            const clockRes = await axios.get(`${API_URL}/api/time-clock/today`);
            if (clockRes.data.status === 'not_started') {
              try {
                await axios.post(`${API_URL}/api/time-clock`, { action: 'clock_in' });
                const updatedClock = await axios.get(`${API_URL}/api/time-clock/today`);
                setClockStatus(updatedClock.data);
              } catch (e) {
                setClockStatus(clockRes.data);
              }
            } else {
              setClockStatus(clockRes.data);
            }
          } catch (e) {}
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const openSmsModal = (reminder, smsType) => {
    let message = '';
    if (smsType === 'reminder') {
      message = `Hello ${reminder.client_name}, this is Supreme Credit. This is a friendly reminder that your scheduled payment of $${reminder.installment_amount} is due on ${reminder.due_date}. Please contact us if you have any questions. Thank you.`;
    } else {
      message = `Hello ${reminder.client_name}, this is Supreme Credit. We are confirming that your payment of $${reminder.installment_amount} has been successfully received and processed. We sincerely appreciate your prompt payment and continued trust in our services. Thank you.`;
    }
    setSmsText(message);
    setSmsModal({ reminder, smsType });
  };

  const handleConfirmSendSms = async () => {
    if (!smsModal) return;
    const { reminder, smsType } = smsModal;
    const key = `${reminder.client_id}-${smsType}`;
    setSendingReminder(key);
    try {
      await axios.post(`${API_URL}/api/payment-reminders/mark-sent`, {
        client_id: reminder.client_id,
        due_date: reminder.due_date,
        amount: reminder.installment_amount,
        sms_type: smsType
      });
      if (smsType === 'reminder') {
        setReminders(prev => prev.map(r =>
          r.client_id === reminder.client_id && r.due_date === reminder.due_date
            ? { ...r, reminder_sent: true }
            : r
        ));
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
    } finally {
      setSendingReminder(null);
      setSmsModal(null);
    }
  };

  const handleClockAction = async (action) => {
    setClockLoading(true);
    try {
      await axios.post(`${API_URL}/api/time-clock`, { action });
      const clockRes = await axios.get(`${API_URL}/api/time-clock/today`);
      setClockStatus(clockRes.data);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Clock action failed';
      toast.error(msg);
      console.error('Clock action error:', msg);
    } finally {
      setClockLoading(false);
    }
  };

  const formatClockTime = (isoStr) => {
    if (!isoStr) return '-';
    try {
      return new Date(isoStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch { return '-'; }
  };

  const avgScoreData = stats?.avg_scores ? [
    { name: 'Experian', score: stats.avg_scores.experian, color: '#1e40af' },
    { name: 'Equifax', score: stats.avg_scores.equifax, color: '#059669' },
    { name: 'TransUnion', score: stats.avg_scores.transunion, color: '#d97706' }
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* TIME CLOCK — Always first, always visible for staff */}
        {isHourlyEmployee && (
          <Card className="bg-[#1a2e5a] shadow-lg" data-testid="time-clock-widget">
            <CardContent className="py-4 px-5">
              <div className="flex flex-col gap-3">
                {/* Status Row */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-white/70" />
                    <span className="text-sm font-semibold text-white">
                      {clockStatus?.clock_out ? 'Day Complete' : clockStatus?.lunch_out && !clockStatus?.lunch_in ? 'On Lunch' : clockStatus?.clock_in ? 'Working' : 'Not Started'}
                    </span>
                    {/* Live Timer */}
                    {clockStatus && clockStatus.clock_in && !clockStatus.clock_out && (
                      <div className="bg-white/10 border border-white/20 text-white px-4 py-1.5 rounded-lg font-mono text-lg tracking-wider" data-testid="live-timer">
                        {clockStatus.lunch_out && !clockStatus.lunch_in ? (
                          <span className="text-amber-300 text-sm font-semibold">PAUSED — ON LUNCH</span>
                        ) : (
                          <span>&#9201; {formatElapsed(elapsedTime)}</span>
                        )}
                      </div>
                    )}
                    {clockStatus?.clock_out && (
                      <span className="text-emerald-300 text-sm font-medium">{clockStatus.total_hours}h paid</span>
                    )}
                  </div>
                  {/* Action Buttons — ONE button at a time (state machine) */}
                  <div className="flex items-center gap-2">
                    {(!clockStatus || clockStatus.status === 'not_started') && (
                      <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-5" onClick={() => handleClockAction('clock_in')} disabled={clockLoading} data-testid="btn-clock-in">
                        {clockLoading ? '...' : 'Clock In / Start'}
                      </Button>
                    )}
                    {clockStatus?.clock_in && !clockStatus?.lunch_out && !clockStatus?.clock_out && (
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white font-medium px-5" onClick={() => handleClockAction('lunch_out')} disabled={clockLoading} data-testid="btn-lunch-out">
                        {clockLoading ? '...' : 'Lunch Out'}
                      </Button>
                    )}
                    {clockStatus?.lunch_out && !clockStatus?.lunch_in && !clockStatus?.clock_out && (
                      <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-5" onClick={() => handleClockAction('lunch_in')} disabled={clockLoading} data-testid="btn-lunch-return">
                        {clockLoading ? '...' : 'Return from Lunch'}
                      </Button>
                    )}
                    {clockStatus?.lunch_in && !clockStatus?.clock_out && (
                      <Button size="sm" className="bg-rose-500 hover:bg-rose-600 text-white font-medium px-5" onClick={() => handleClockAction('clock_out')} disabled={clockLoading} data-testid="btn-clock-out">
                        {clockLoading ? '...' : 'Clock Out'}
                      </Button>
                    )}
                  </div>
                </div>
                {/* Time Details Row */}
                {clockStatus && clockStatus.status !== 'not_started' && (
                  <div className="flex flex-wrap gap-6 text-xs text-white/70 border-t border-white/10 pt-2">
                    <span>Clock In: <strong className="text-white">{formatClockTime(clockStatus.clock_in)}</strong></span>
                    {clockStatus.lunch_out && <span>Lunch Out: <strong className="text-amber-300">{formatClockTime(clockStatus.lunch_out)}</strong></span>}
                    {clockStatus.lunch_in && <span>Return: <strong className="text-blue-300">{formatClockTime(clockStatus.lunch_in)}</strong></span>}
                    {clockStatus.clock_out && <span>Clock Out: <strong className="text-rose-300">{formatClockTime(clockStatus.clock_out)}</strong></span>}
                    {clockStatus.lunch_minutes > 0 && <span>Lunch: <strong className="text-white">{clockStatus.lunch_minutes} min</strong></span>}
                    {clockStatus.total_hours != null && <span>Total Paid: <strong className="text-emerald-300">{clockStatus.total_hours}h</strong></span>}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logo + Header */}
        <div className="flex flex-col items-center text-center pt-4 pb-2">
          <img
            src="/supreme-logo.png"
            alt="Supreme Credit Inc."
            className="h-52 w-auto mb-6"
            data-testid="supreme-logo"
          />
          <h1 className="text-xl font-semibold text-slate-900">Welcome back, {user?.name}</h1>
        </div>

        {/* Quick Actions */}
        <div className="flex justify-center gap-3">
          <Button
            className="bg-[#1a2e5a] hover:bg-[#15254a] text-white px-6 py-2.5 text-sm font-medium"
            onClick={() => navigate('/admin/clients?action=add')}
            data-testid="add-client-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
          <Button
            variant="outline"
            className="border-[#1a2e5a] text-[#1a2e5a] hover:bg-[#1a2e5a] hover:text-white px-6 py-2.5 text-sm font-medium"
            onClick={() => navigate('/admin/cadastro-pdf')}
            data-testid="cadastro-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Cadastro by PDF
          </Button>
        </div>

        {/* Client Status Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="status-summary">
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/admin/clients?status=active')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/admin/clients?status=active')}
            className="cursor-pointer"
            data-testid="stat-active-clients"
          >
            <Card className="bg-white border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">
              <CardContent className="px-3 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500 leading-tight">Active Clients</p>
                    <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats?.active_clients || 0}</p>
                  </div>
                  <CheckCircle className="w-6 h-6 text-emerald-500 opacity-60" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/admin/clients?status=collection')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/admin/clients?status=collection')}
            className="cursor-pointer"
            data-testid="stat-collection-attention"
          >
            <Card className="bg-white border-l-4 border-l-amber-500 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">
              <CardContent className="px-3 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500 leading-tight">Collection Attention</p>
                    <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats?.collection_attention || 0}</p>
                  </div>
                  <AlertTriangle className="w-6 h-6 text-amber-500 opacity-60" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/admin/clients?status=pending')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/admin/clients?status=pending')}
            className="cursor-pointer"
            data-testid="stat-pending-payment"
          >
            <Card className="bg-white border-l-4 border-l-blue-500 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">
              <CardContent className="px-3 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500 leading-tight">Pending Payment</p>
                    <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats?.pending_payment || 0}</p>
                  </div>
                  <Clock className="w-6 h-6 text-blue-500 opacity-60" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/admin/clients')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/admin/clients')}
            className="cursor-pointer"
            data-testid="stat-total-clients"
          >
            <Card className="bg-white border-l-4 border-l-slate-400 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all">
              <CardContent className="px-3 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500 leading-tight">Total Clients</p>
                    <p className="text-2xl font-bold text-slate-900 mt-0.5">{stats?.total_clients || 0}</p>
                  </div>
                  <Users className="w-6 h-6 text-slate-400 opacity-60" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 1. Payment Reminder Calendar — FIRST */}
        {isFinanceRole && (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="payment-reminders-section">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-lg">Payment Reminder Calendar</CardTitle>
                {reminders.length > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 text-xs ml-2">{reminders.length} due</Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">Clients with payments due within 48 hours — send SMS manually</p>
            </CardHeader>
            <CardContent>
              {reminders.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-8 h-8 mx-auto text-emerald-300 mb-2" />
                  <p className="text-slate-500 text-sm">No payment reminders due</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reminders.map((r) => (
                    <div
                      key={`${r.client_id}-${r.due_date}`}
                      className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`reminder-${r.client_id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900">{r.client_name}</p>
                            {r.reminder_sent && (
                              <Badge className="bg-emerald-100 text-emerald-800 text-xs">Reminder Sent</Badge>
                            )}
                          </div>
                          {r.company_name && (
                            <p className="text-sm text-slate-500">{r.company_name}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-slate-600">
                            <span>Due: <strong>{r.due_date}</strong></span>
                            <span>Amount: <strong>${r.installment_amount}</strong></span>
                            <span>Installment: <strong>#{r.installment_number}/{r.total_installments}</strong></span>
                            <span>Remaining: <strong>{r.remaining_installments}</strong></span>
                          </div>
                          {r.phone && <p className="text-xs text-slate-400 mt-1">Phone: {r.phone}</p>}
                        </div>
                        <Button
                          size="sm"
                          className={
                            r.reminder_sent
                              ? 'bg-slate-100 text-slate-500 hover:bg-slate-100 cursor-default'
                              : 'bg-amber-500 hover:bg-amber-600 text-white'
                          }
                          disabled={r.reminder_sent || sendingReminder === `${r.client_id}-reminder`}
                          onClick={() => openSmsModal(r, 'reminder')}
                          data-testid={`send-reminder-${r.client_id}`}
                        >
                          {sendingReminder === `${r.client_id}-reminder` ? (
                            <Clock className="w-4 h-4 mr-1 animate-spin" />
                          ) : r.reminder_sent ? (
                            <CheckCircle className="w-4 h-4 mr-1" />
                          ) : (
                            <Bell className="w-4 h-4 mr-1" />
                          )}
                          {r.reminder_sent ? 'Reminder Sent' : 'Send Payment Reminder SMS'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 2. Finance Summary — RIGHT AFTER reminders */}
        {isFinanceRole && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="finance-summary">
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="finance-received">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-3 h-12 rounded-full bg-emerald-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">Received This Month</p>
                    <p className="text-2xl font-bold text-slate-900">${(stats?.total_received || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="finance-pending">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-3 h-12 rounded-full bg-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">Pending</p>
                    <p className="text-2xl font-bold text-slate-900">
                      ${((stats?.total_expected_month || 0) - (stats?.total_received || 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="finance-overdue">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="w-3 h-12 rounded-full bg-rose-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">Overdue Accounts</p>
                    <p className="text-2xl font-bold text-slate-900">{stats?.overdue_count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 3. Average Credit Score Chart — AFTER finance */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="avg-score-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900">Average Credit Scores</CardTitle>
                <p className="text-sm text-slate-500 mt-0.5">
                  Across {stats?.clients_with_scores || 0} clients with score data
                </p>
              </div>
              <TrendingUp className="w-5 h-5 text-slate-400" />
            </div>
          </CardHeader>
          <CardContent>
            {avgScoreData.length > 0 && avgScoreData.some(d => d.score > 0) ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={avgScoreData} barSize={60}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={13} fontWeight={500} />
                    <YAxis domain={[300, 850]} stroke="#64748b" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                      formatter={(value) => [value, 'Average Score']}
                    />
                    <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                      {avgScoreData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                No score data available yet
              </div>
            )}
            {avgScoreData.length > 0 && avgScoreData.some(d => d.score > 0) && (
              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
                {avgScoreData.map((s) => (
                  <div key={s.name} className="text-center">
                    <p className="text-xs text-slate-500 uppercase font-medium">{s.name}</p>
                    <p className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.score}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. Recent Clients */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="recent-clients">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900">Recent Clients</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/clients')}
              data-testid="manage-clients-btn"
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentClients.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No clients found</p>
              ) : (
                recentClients.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                    data-testid={`client-row-${client.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#1a2e5a]/10 flex items-center justify-center">
                        <span className="text-xs font-semibold text-[#1a2e5a]">
                          {client.first_name?.[0]}{client.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900">{client.first_name} {client.last_name}</p>
                        <p className="text-xs text-slate-500">{client.client_code} · {client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={
                        client.status === 'Active Client' || client.status === 'active'
                          ? 'bg-emerald-100 text-emerald-800 text-xs'
                          : client.status === 'Collection Attention'
                          ? 'bg-amber-100 text-amber-800 text-xs'
                          : 'bg-slate-100 text-slate-800 text-xs'
                      }>
                        {client.status}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/clients/${client.id}`)}
                        data-testid={`view-client-btn-${client.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* 5. Finance Quick Link */}
        {isFinanceRole && (
          <Card
            className="bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/finance')}
            data-testid="quick-action-finance"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Finance Dashboard</p>
                <p className="text-sm text-slate-500">Payment tracking & management</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* SMS Confirmation Modal */}
      <Dialog open={!!smsModal} onOpenChange={(open) => { if (!open) setSmsModal(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="sms-modal-title">
              {smsModal?.smsType === 'confirmation' ? 'Send Payment Confirmation SMS' : 'Send Payment Reminder SMS'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>To: <strong>{smsModal?.reminder?.client_name}</strong></span>
              {smsModal?.reminder?.phone && (
                <span className="text-slate-400">({smsModal.reminder.phone})</span>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">SMS Message:</p>
              <Textarea
                value={smsText}
                onChange={(e) => setSmsText(e.target.value)}
                rows={5}
                className="text-sm"
                data-testid="sms-text-editor"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSmsModal(null)}
              data-testid="sms-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-[#1a2e5a] hover:bg-[#15254a] text-white"
              onClick={handleConfirmSendSms}
              disabled={!!sendingReminder}
              data-testid="sms-send-btn"
            >
              {sendingReminder ? (
                <Clock className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <MessageSquare className="w-4 h-4 mr-1" />
              )}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
